export function normalizeTeamName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function teamsMatch(game, bet) {
  const home = normalizeTeamName(bet.homeTeam);
  const away = normalizeTeamName(bet.awayTeam);
  const gHome = normalizeTeamName(game.home_team);
  const gAway = normalizeTeamName(game.away_team);

  return (
    (gHome === home && gAway === away) || (gHome === away && gAway === home)
  );
}

/** Find live odds game row for a straight bet ticket. */
export function findGameForStraightBet(games = [], bet) {
  if (!bet || !Array.isArray(games) || games.length === 0) return null;

  if (bet.gameId) {
    const byId = games.find((game) => game?.id === bet.gameId);
    if (byId) return byId;
  }

  const sportKey = bet.keyName || "";
  return (
    games.find((game) => {
      if (!teamsMatch(game, bet)) return false;
      if (sportKey && game.sport_key && game.sport_key !== sportKey) {
        return false;
      }
      return true;
    }) || null
  );
}

/** Minimal game shape when live board data is unavailable. */
export function buildFallbackGameFromBet(bet) {
  if (!bet) return null;

  const homeTeam = bet.homeTeam || "";
  const awayTeam = bet.awayTeam || "";
  const score1 = Number(bet.score1);
  const score2 = Number(bet.score2);
  const hasStoredScores =
    (Number.isFinite(score1) && score1 > 0) ||
    (Number.isFinite(score2) && score2 > 0);

  return {
    id: bet.gameId || "",
    sport_key: bet.keyName || "",
    sport_title: bet.sport_title || bet.title || "",
    home_team: homeTeam,
    away_team: awayTeam,
    commence_time: bet.gameTime || "",
    bookmakers: [],
    scores: hasStoredScores
      ? [
          { name: homeTeam, score: String(score1) },
          { name: awayTeam, score: String(score2) },
        ]
      : undefined,
    _fallback: true,
  };
}

export function resolveGameForStraightBet(games = [], bet) {
  return findGameForStraightBet(games, bet) || buildFallbackGameFromBet(bet);
}

/** @deprecated Single-expand toggle; prefer toggleExpandedTicketIds for Open Bets. */
export function toggleExpandedTicket(currentId, ticketId) {
  if (!ticketId) return null;
  return currentId === ticketId ? null : ticketId;
}

export function getExpandableTicketIds(orders = []) {
  if (!Array.isArray(orders)) return [];
  return orders
    .filter(isActiveBetExpandable)
    .map((order) => order?.ticketId)
    .filter((ticketId) => ticketId != null && String(ticketId).length > 0);
}

export function toggleExpandedTicketIds(currentIds = [], ticketId) {
  if (!ticketId) return Array.isArray(currentIds) ? [...currentIds] : [];
  const next = new Set(Array.isArray(currentIds) ? currentIds : []);
  if (next.has(ticketId)) next.delete(ticketId);
  else next.add(ticketId);
  return Array.from(next);
}

export function areAllExpandableExpanded(currentIds = [], orders = []) {
  const expandable = getExpandableTicketIds(orders);
  if (expandable.length === 0) return false;
  const expanded = new Set(Array.isArray(currentIds) ? currentIds : []);
  return expandable.every((ticketId) => expanded.has(ticketId));
}

export function hasAnyExpandedBoards(currentIds = []) {
  return Array.isArray(currentIds) && currentIds.length > 0;
}

/** Individual legs on a straight, parlay, or teaser ticket. */
export function getActiveBetLegs(bet) {
  if (!bet) return [];
  if (bet.betType === "Straight") return [bet];
  if (Array.isArray(bet.bettingData)) {
    return bet.bettingData.filter(
      (leg) => leg?.homeTeam || leg?.awayTeam || leg?.gameId
    );
  }
  return [];
}

export function isActiveBetExpandable(bet) {
  if (!bet?.betType) return false;
  if (bet.betType === "Straight") return true;
  if (bet.betType === "Parlay" || bet.betType === "Teaser") {
    return getActiveBetLegs(bet).length > 0;
  }
  return false;
}

/** @deprecated Use isActiveBetExpandable */
export function isStraightBetExpandable(bet) {
  return isActiveBetExpandable(bet);
}
